/**
 * UniversalSignDialog — handles the OTP-based signing flow for ALL 6 doc
 * kinds (contract, epi, badge, vt, vacation_notice, vacation_advance).
 *
 * For VT, an extra "choice step" (SIM/NÃO + sub-options) is shown BEFORE
 * the OTP is sent. For all other kinds, the user just confirms reading and
 * proceeds.
 */
import { useEffect, useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { motion } from "framer-motion";
import {
  Dialog, DialogContent, DialogDescription, DialogFooter,
  DialogHeader, DialogTitle,
} from "./ui/dialog";
import { Button } from "./ui/button";
import { Input } from "./ui/input";
import { Label } from "./ui/label";
import { Checkbox } from "./ui/checkbox";
import { Textarea } from "./ui/textarea";
import SignatureEditDialog from "./SignatureEditDialog";
import { FaceSignCapture } from "./FaceSignCapture";
import {
  FileSignature, KeyRound, Loader2, CheckCircle2, Download,
  ShieldCheck, Phone, Mail, Check, Bus, HardHat, IdCard, Plane, ScanFace,
} from "lucide-react";
import { API, useAuth } from "../App";

const KIND_META = {
  contract: {
    label: "Contrato de Experiência", icon: FileSignature, color: "sky",
    base: "contracts", idParam: "contract_id",
  },
  epi: {
    label: "Ficha de EPI", icon: HardHat, color: "amber",
    base: "epi-records", idParam: "rec_id",
  },
  badge: {
    label: "Declaração de Crachá", icon: IdCard, color: "sky",
    base: "badge-declarations", idParam: "rec_id",
  },
  vt: {
    label: "Vale Transporte", icon: Bus, color: "blue",
    base: "vt-declarations", idParam: "rec_id",
    hasChoice: true,
  },
  nao_adesao: {
    label: "Declaração de Adesão ao Plano de Saúde",
    icon: FileSignature, color: "emerald",
    base: "nao-adesao", idParam: "rec_id",
    hasNaoAdesao: true,
  },
  termino_contrato: {
    label: "Rescisão Contratual (TRCT + Comunicado)",
    icon: FileSignature, color: "violet",
    base: "termino-contrato", idParam: "doc_id",
  },
  // Termino de Contrato — split into 3 independent signature units.
  // Each shares the same base endpoint but sets `piece` on every
  // request so the backend signs only that specific PDF.
  termino_comunicado: {
    label: "Comunicado de Rescisão",
    icon: FileSignature, color: "violet",
    base: "termino-contrato", idParam: "doc_id",
    piece: "comunicado",
  },
  termino_trct: {
    label: "TRCT + Homologação",
    icon: FileSignature, color: "blue",
    base: "termino-contrato", idParam: "doc_id",
    piece: "trct",
  },
  termino_protocolo: {
    label: "Protocolo de Kit Demissional",
    icon: FileSignature, color: "emerald",
    base: "termino-contrato", idParam: "doc_id",
    piece: "protocolo",
  },
  vacation_notice: {
    label: "Recibo de Férias", icon: Plane, color: "emerald",
    base: "vacation-notices", idParam: "recipient_id",
  },
  vacation_advance: {
    label: "Aviso de Férias", icon: Plane, color: "violet",
    base: "vacation-advances", idParam: "recipient_id",
  },
  // Kit Admissão extras — all share the same /kit-documents endpoints
  // (the backend dispatches by kind).
  kit_integracao_matriz: {
    label: "Integração — Matriz", icon: FileSignature, color: "sky",
    base: "kit-documents", idParam: "doc_id",
  },
  kit_integracao_filial: {
    label: "Integração — Filial", icon: FileSignature, color: "sky",
    base: "kit-documents", idParam: "doc_id",
  },
  kit_lgpd: {
    label: "Termo LGPD", icon: FileSignature, color: "violet",
    base: "kit-documents", idParam: "doc_id",
  },
  kit_ordem_servico: {
    label: "Ordem de Serviço SST", icon: HardHat, color: "amber",
    base: "kit-documents", idParam: "doc_id",
  },
  kit_ficha_registro: {
    label: "Ficha de Registro", icon: FileSignature, color: "violet",
    base: "kit-documents", idParam: "doc_id",
  },
};

// Static class lookups — Tailwind's JIT needs full literal class strings at
// build time, so we cannot interpolate `bg-${accent}-700`. This map exposes
// every color combination we actually use.
const COLOR_CLASSES = {
  sky: {
    title: "text-sky-800",
    border: "border-sky-200",
    bgLight: "bg-sky-50",
    text: "text-sky-700",
    textDark: "text-sky-900",
    borderActive: "border-sky-600",
    primaryBtn: "bg-sky-700 hover:bg-sky-800 text-white",
  },
  amber: {
    title: "text-amber-800",
    border: "border-amber-200",
    bgLight: "bg-amber-50",
    text: "text-amber-700",
    textDark: "text-amber-900",
    borderActive: "border-amber-600",
    primaryBtn: "bg-amber-600 hover:bg-amber-700 text-white",
  },
  blue: {
    title: "text-blue-800",
    border: "border-blue-200",
    bgLight: "bg-blue-50",
    text: "text-blue-700",
    textDark: "text-blue-900",
    borderActive: "border-blue-600",
    primaryBtn: "bg-blue-700 hover:bg-blue-800 text-white",
  },
  emerald: {
    title: "text-emerald-800",
    border: "border-emerald-200",
    bgLight: "bg-emerald-50",
    text: "text-emerald-700",
    textDark: "text-emerald-900",
    borderActive: "border-emerald-600",
    primaryBtn: "bg-emerald-700 hover:bg-emerald-800 text-white",
  },
  violet: {
    title: "text-violet-800",
    border: "border-violet-200",
    bgLight: "bg-violet-50",
    text: "text-violet-700",
    textDark: "text-violet-900",
    borderActive: "border-violet-600",
    primaryBtn: "bg-violet-700 hover:bg-violet-800 text-white",
  },
};

const maskPhone = (p) => {
  if (!p) return "—";
  const d = String(p).replace(/\D/g, "");
  if (d.length < 4) return p;
  return `••• ••• ${d.slice(-4)}`;
};
const maskEmail = (e) => {
  if (!e || !e.includes("@")) return "—";
  const [u, dom] = e.split("@");
  return `${u.slice(0, 2)}***@${dom}`;
};

export default function UniversalSignDialog({
  kind, id, open, onClose, onSigned,
  /** For nao_adesao only: "com_dependentes" | "sem_dependentes" */
  variant,
  /** Force the modal to be non-dismissable (blocker mode). */
  blocking = false,
}) {
  const { token, user } = useAuth();
  const meta = KIND_META[kind];
  const isNoDeps = kind === "nao_adesao" && variant === "sem_dependentes";

  const [step, setStep] = useState("preview");
  const [rubricaOpen, setRubricaOpen] = useState(false);
  const [previewUrl, setPreviewUrl] = useState("");
  const [busy, setBusy] = useState(false);
  const [agreed, setAgreed] = useState(false);
  const [faceIntent, setFaceIntent] = useState(false);
  const faceEnabled = true; // facial disponível em todos os documentos
  const [channel, setChannel] = useState("sms");
  const [registeredPhone, setRegisteredPhone] = useState("");
  const [registeredEmail, setRegisteredEmail] = useState("");
  const [maskedTo, setMaskedTo] = useState("");
  const [code, setCode] = useState("");

  // VT choice state
  const [opcao, setOpcao] = useState("");
  const [tipo, setTipo] = useState("");
  const [motivo, setMotivo] = useState("");
  const [justificativa, setJustificativa] = useState("");

  // Não Adesão choice state
  const [interesse, setInteresse] = useState("");           // "tem" | "nao_tem"
  const [temDeps, setTemDeps] = useState("");               // "sim" | "nao"
  const [depsCount, setDepsCount] = useState(0);            // 1..6
  const [deps, setDeps] = useState([]);                     // array of dep objects

  useEffect(() => {
    if (!open || !meta || !id) return;
    setStep("preview");
    setCode(""); setBusy(false); setChannel("sms");
    setMaskedTo(""); setAgreed(false);
    setOpcao(""); setTipo(""); setMotivo(""); setJustificativa("");
    setInteresse(""); setTemDeps(""); setDepsCount(0); setDeps([]);
    axios.get(`${API}/employee/profile`, { params: { token }, timeout: 10000 })
      .then((r) => {
        setRegisteredPhone((r.data?.telefone || "").trim());
        setRegisteredEmail((r.data?.email || "").trim());
      }).catch(() => {});
    axios.get(`${API}/employee/${meta.base}/${id}/preview`, {
      params: { token, ...(meta.piece ? { piece: meta.piece } : {}) },
      responseType: "blob", timeout: 30000,
    }).then((r) => setPreviewUrl(URL.createObjectURL(r.data)))
      .catch(() => setPreviewUrl(""));
    return () => { if (previewUrl) URL.revokeObjectURL(previewUrl); };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, kind, id, token]);

  if (!meta || !id) return null;
  const Icon = meta.icon;
  const accent = meta.color;
  const cc = COLOR_CLASSES[accent] || COLOR_CLASSES.sky;
  const canSms = !!registeredPhone;
  const canEmail = !!registeredEmail;

  const choiceValid = () => {
    if (meta.hasChoice) {
      if (opcao === "sim") return !!tipo;
      if (opcao === "nao") {
        if (!motivo) return false;
        if (motivo === "outros") return justificativa.trim().length >= 5;
        return true;
      }
      return false;
    }
    if (meta.hasNaoAdesao) {
      if (interesse === "nao_tem") return true;
      if (interesse !== "tem") return false;
      // Sem-dependentes variant: employee only declares "TENHO interesse"
      if (isNoDeps) return true;
      if (temDeps === "nao") return true;
      if (temDeps !== "sim") return false;
      if (depsCount < 1 || depsCount > 6) return false;
      // Every dependent must have nome, parentesco and data_nascimento
      for (let i = 0; i < depsCount; i++) {
        const d = deps[i] || {};
        if (!(d.nome || "").trim()) return false;
        if (!(d.parentesco || "").trim()) return false;
        if (!(d.data_nascimento || "").trim()) return false;
        const cpfDigits = (d.cpf || "").replace(/\D/g, "");
        if (cpfDigits.length && cpfDigits.length !== 11) return false;
      }
      return true;
    }
    return true;
  };

  const canSend = () => {
    if (!agreed && !meta.hasChoice && !meta.hasNaoAdesao) return false;
    if ((meta.hasChoice || meta.hasNaoAdesao) && !choiceValid()) return false;
    if (channel === "sms" && !canSms) return false;
    if (channel === "email" && !canEmail) return false;
    return true;
  };

  const buildChoicePayload = () => {
    if (meta.hasChoice) {
      const obj = { opcao };
      if (opcao === "sim") obj.tipo_conducao = tipo;
      if (opcao === "nao") {
        obj.motivo_recusa = motivo;
        if (motivo === "outros") obj.justificativa_outros = justificativa.trim();
      }
      return obj;
    }
    if (meta.hasNaoAdesao) {
      const obj = { interesse };
      if (interesse === "tem" && !isNoDeps) {
        obj.tem_dependentes = temDeps === "sim";
        if (temDeps === "sim") {
          obj.dependentes = deps.slice(0, depsCount).map((d) => ({
            nome: (d.nome || "").trim(),
            parentesco: d.parentesco,
            data_nascimento: d.data_nascimento,
            cpf: (d.cpf || "").replace(/\D/g, ""),
          }));
        }
      }
      if (isNoDeps) {
        obj.tem_dependentes = false;
      }
      return obj;
    }
    return {};
  };

  const sendOtp = async () => {
    if (!canSend()) return toast.error("Complete os campos antes de enviar");
    setBusy(true);
    try {
      const r = await axios.post(
        `${API}/employee/${meta.base}/${id}/send-otp`,
        { token, channel,
          ...(meta.piece ? { piece: meta.piece } : {}),
          ...buildChoicePayload() }, { timeout: 30000 },
      );
      setMaskedTo(r.data?.to_masked || "");
      setStep("otp");
      toast.success(`Código enviado por ${channel === "email" ? "e-mail" : "SMS"}`);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Falha ao enviar código");
    } finally { setBusy(false); }
  };

  const confirmOtp = async () => {
    if (!code || code.length < 4) return toast.error("Digite o código");
    setBusy(true);
    try {
      await axios.post(
        `${API}/employee/${meta.base}/${id}/confirm-otp`,
        { token, code, channel,
          ...(meta.piece ? { piece: meta.piece } : {}) },
        { timeout: 30000 },
      );
      setStep("done");
      toast.success(`${meta.label} assinada!`);
      onSigned && onSigned();
    } catch (e) {
      const detail = e.response?.data?.detail || "";
      if (detail.toLowerCase().includes("rúbrica")) {
        setRubricaOpen(true);
      }
      toast.error(detail || "Código inválido");
    } finally { setBusy(false); }
  };

  const download = async () => {
    try {
      const r = await axios.get(`${API}/employee/${meta.base}/${id}/pdf`, {
        params: { token, ...(meta.piece ? { piece: meta.piece } : {}) },
        responseType: "blob", timeout: 30000,
      });
      const url = URL.createObjectURL(r.data);
      const a = document.createElement("a");
      a.href = url; a.download = `${kind}_${id.slice(0, 8)}.pdf`; a.click();
      URL.revokeObjectURL(url);
    } catch { toast.error("Falha ao baixar"); }
  };

  // When blocking=true, prevent close-on-backdrop. We allow close only via
  // onClose called explicitly (e.g., success "Concluir" button).
  const handleOpenChange = (v) => {
    if (!v) {
      if (blocking && step !== "done") return;
      onClose && onClose();
    }
  };

  return (
    <Dialog open={open} onOpenChange={handleOpenChange}>
      <DialogContent
        className="max-w-3xl w-[95vw] max-h-[92vh] overflow-y-auto"
        onPointerDownOutside={(e) => blocking && step !== "done" && e.preventDefault()}
        onEscapeKeyDown={(e) => blocking && step !== "done" && e.preventDefault()}
        data-testid={`universal-sign-${kind}`}>
        <DialogHeader>
          <DialogTitle className={`flex items-center gap-2 ${cc.title}`}>
            <Icon className="w-5 h-5" />
            {meta.label}
          </DialogTitle>
          <DialogDescription>
            {step === "preview" && (meta.hasChoice
              ? "Leia o documento e escolha SIM ou NÃO antes de assinar."
              : meta.hasNaoAdesao
              ? "Leia o documento, informe se tem interesse no plano de saúde e cadastre os dependentes (se houver) antes de assinar."
              : "Leia o documento e marque a opção 'Concordo' antes de assinar.")}
            {step === "otp" && "Digite o código enviado para confirmar a assinatura."}
            {step === "done" && "Assinatura registrada com validade jurídica."}
          </DialogDescription>
        </DialogHeader>

        {step === "preview" && (
          <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }}
                      className="space-y-3">
            {previewUrl ? (
              <iframe src={previewUrl} title={meta.label}
                className="w-full h-[42vh] border border-slate-200 rounded-md" />
            ) : (
              <div className="h-[42vh] flex items-center justify-center bg-slate-50 rounded-md">
                <Loader2 className="w-6 h-6 animate-spin text-slate-400" />
              </div>
            )}

            {/* VT choice block */}
            {meta.hasChoice && (
              <div className={`rounded-md border ${cc.border} ${cc.bgLight} p-3 space-y-3`}>
                <div className={`text-xs font-bold ${cc.textDark}`}>
                  Sua escolha sobre o Vale Transporte:
                </div>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                  <button type="button" onClick={() => setOpcao("sim")}
                    className={`text-left rounded-md border-2 p-3 transition ${
                      opcao === "sim"
                        ? "border-emerald-600 bg-emerald-50 shadow-sm"
                        : "border-slate-200 bg-white hover:border-slate-300"
                    }`} data-testid="universal-vt-opcao-sim">
                    <div className="flex items-center gap-2 mb-2">
                      <div className={`w-4 h-4 rounded-full border-2 flex items-center justify-center ${
                        opcao === "sim" ? "border-emerald-600 bg-emerald-600" : "border-slate-400"
                      }`}>
                        {opcao === "sim" && <Check className="w-3 h-3 text-white" />}
                      </div>
                      <span className="font-bold text-sm">SIM — quero receber</span>
                    </div>
                    {opcao === "sim" && (
                      <div className="space-y-1.5 pl-6">
                        {[
                          ["municipal", "Municipal"],
                          ["intermunicipal", "Intermunicipal"],
                          ["empresa", "Fornecido pela empresa"],
                        ].map(([k, lbl]) => (
                          <label key={k} className="flex items-center gap-2 text-xs cursor-pointer">
                            <input type="radio" name="tipo" value={k}
                              checked={tipo === k} onChange={() => setTipo(k)} />
                            {lbl}
                          </label>
                        ))}
                      </div>
                    )}
                  </button>
                  <button type="button" onClick={() => setOpcao("nao")}
                    className={`text-left rounded-md border-2 p-3 transition ${
                      opcao === "nao"
                        ? "border-slate-700 bg-slate-100 shadow-sm"
                        : "border-slate-200 bg-white hover:border-slate-300"
                    }`} data-testid="universal-vt-opcao-nao">
                    <div className="flex items-center gap-2 mb-2">
                      <div className={`w-4 h-4 rounded-full border-2 flex items-center justify-center ${
                        opcao === "nao" ? "border-slate-700 bg-slate-700" : "border-slate-400"
                      }`}>
                        {opcao === "nao" && <Check className="w-3 h-3 text-white" />}
                      </div>
                      <span className="font-bold text-sm">NÃO — declino</span>
                    </div>
                    {opcao === "nao" && (
                      <div className="space-y-1.5 pl-6">
                        {[
                          ["proprio", "Transporte próprio"],
                          ["residencia", "Resido próximo ao trabalho"],
                          ["outros", "Outros (especificar)"],
                        ].map(([k, lbl]) => (
                          <label key={k} className="flex items-center gap-2 text-xs cursor-pointer">
                            <input type="radio" name="motivo" value={k}
                              checked={motivo === k} onChange={() => setMotivo(k)} />
                            {lbl}
                          </label>
                        ))}
                        {motivo === "outros" && (
                          <Textarea placeholder="Justificativa (mín. 5 caracteres)"
                            value={justificativa}
                            onChange={(e) => setJustificativa(e.target.value)}
                            rows={2} className="mt-2 text-xs" />
                        )}
                      </div>
                    )}
                  </button>
                </div>
              </div>
            )}

            {/* Não Adesão ao Plano de Saúde — interactive choice */}
            {meta.hasNaoAdesao && (
              <div className={`rounded-md border ${cc.border} ${cc.bgLight} p-3 space-y-3`}>
                <div className={`text-xs font-bold ${cc.textDark}`}>
                  Deseja aderir ao plano de saúde empresarial?
                </div>
                {/* Interesse — TENHO / NÃO TENHO */}
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                  <button type="button" onClick={() => setInteresse("tem")}
                    className={`text-left rounded-md border-2 p-3 transition ${
                      interesse === "tem"
                        ? "border-emerald-600 bg-emerald-50 shadow-sm"
                        : "border-slate-200 bg-white hover:border-slate-300"
                    }`} data-testid="universal-na-interesse-tem">
                    <div className="flex items-center gap-2">
                      <div className={`w-4 h-4 rounded-full border-2 flex items-center justify-center ${
                        interesse === "tem" ? "border-emerald-600 bg-emerald-600" : "border-slate-400"
                      }`}>
                        {interesse === "tem" && <Check className="w-3 h-3 text-white" />}
                      </div>
                      <span className="font-bold text-sm">TENHO interesse em aderir</span>
                    </div>
                  </button>
                  <button type="button" onClick={() => setInteresse("nao_tem")}
                    className={`text-left rounded-md border-2 p-3 transition ${
                      interesse === "nao_tem"
                        ? "border-slate-700 bg-slate-100 shadow-sm"
                        : "border-slate-200 bg-white hover:border-slate-300"
                    }`} data-testid="universal-na-interesse-naotem">
                    <div className="flex items-center gap-2">
                      <div className={`w-4 h-4 rounded-full border-2 flex items-center justify-center ${
                        interesse === "nao_tem" ? "border-slate-700 bg-slate-700" : "border-slate-400"
                      }`}>
                        {interesse === "nao_tem" && <Check className="w-3 h-3 text-white" />}
                      </div>
                      <span className="font-bold text-sm">NÃO TENHO interesse</span>
                    </div>
                  </button>
                </div>

                {/* Dependentes step (only when TEM interesse AND variant permite) */}
                {interesse === "tem" && !isNoDeps && (
                  <div className="pt-2 border-t border-slate-200 space-y-3">
                    <div className={`text-xs font-bold ${cc.textDark}`}>
                      Possui dependentes para incluir no plano?
                    </div>
                    <div className="flex gap-2">
                      {[["sim", "SIM"], ["nao", "NÃO"]].map(([k, lbl]) => (
                        <button key={k} type="button"
                          onClick={() => { setTemDeps(k); if (k === "nao") { setDepsCount(0); setDeps([]); } }}
                          className={`flex-1 rounded-md border-2 py-2 px-3 text-sm font-semibold transition ${
                            temDeps === k
                              ? "border-emerald-600 bg-emerald-50"
                              : "border-slate-200 bg-white hover:border-slate-300"
                          }`}
                          data-testid={`universal-na-tem-deps-${k}`}>
                          {lbl}
                        </button>
                      ))}
                    </div>

                    {temDeps === "sim" && (
                      <>
                        <div>
                          <div className="text-xs text-slate-600 mb-1">
                            Quantos dependentes?
                          </div>
                          <div className="flex gap-1.5">
                            {[1, 2, 3, 4, 5, 6].map((n) => (
                              <button key={n} type="button"
                                onClick={() => {
                                  setDepsCount(n);
                                  setDeps((prev) => {
                                    const next = [...prev];
                                    while (next.length < n) {
                                      next.push({ nome: "", parentesco: "", data_nascimento: "", cpf: "" });
                                    }
                                    return next.slice(0, n);
                                  });
                                }}
                                className={`w-9 h-9 rounded-md border-2 text-sm font-semibold transition ${
                                  depsCount === n
                                    ? "border-emerald-600 bg-emerald-600 text-white"
                                    : "border-slate-200 bg-white hover:border-slate-300"
                                }`}
                                data-testid={`universal-na-deps-count-${n}`}>
                                {n}
                              </button>
                            ))}
                          </div>
                        </div>

                        {depsCount > 0 && (
                          <div className="space-y-3">
                            {Array.from({ length: depsCount }).map((_, i) => {
                              const d = deps[i] || {};
                              const updateDep = (patch) => setDeps((prev) => {
                                const next = [...prev];
                                next[i] = { ...(next[i] || {}), ...patch };
                                return next;
                              });
                              return (
                                <div key={i} className="rounded-md border border-slate-200 bg-white p-3 space-y-2">
                                  <div className="text-[11px] font-semibold text-slate-600">
                                    DEPENDENTE {i + 1}
                                  </div>
                                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
                                    <div>
                                      <Label className="text-[11px] text-slate-600">Nome completo</Label>
                                      <Input value={d.nome || ""}
                                        onChange={(e) => updateDep({ nome: e.target.value })}
                                        placeholder="Nome do dependente"
                                        className="h-9 text-sm"
                                        data-testid={`universal-na-dep-${i}-nome`} />
                                    </div>
                                    <div>
                                      <Label className="text-[11px] text-slate-600">Parentesco</Label>
                                      <select value={d.parentesco || ""}
                                        onChange={(e) => updateDep({ parentesco: e.target.value })}
                                        className="h-9 w-full rounded-md border border-slate-300 bg-white px-2 text-sm"
                                        data-testid={`universal-na-dep-${i}-parentesco`}>
                                        <option value="">Selecione…</option>
                                        <option value="conjuge">Cônjuge</option>
                                        <option value="companheiro">Companheiro(a)</option>
                                        <option value="filho">Filho(a)</option>
                                        <option value="enteado">Enteado(a)</option>
                                        <option value="pai">Pai</option>
                                        <option value="mae">Mãe</option>
                                        <option value="outro">Outro</option>
                                      </select>
                                    </div>
                                    <div>
                                      <Label className="text-[11px] text-slate-600">Data de nascimento</Label>
                                      <Input type="date" value={d.data_nascimento || ""}
                                        onChange={(e) => updateDep({ data_nascimento: e.target.value })}
                                        className="h-9 text-sm"
                                        data-testid={`universal-na-dep-${i}-dnasc`} />
                                    </div>
                                    <div>
                                      <Label className="text-[11px] text-slate-600">CPF (opcional)</Label>
                                      <Input value={d.cpf || ""}
                                        onChange={(e) => updateDep({ cpf: e.target.value })}
                                        placeholder="000.000.000-00" maxLength={14}
                                        className="h-9 text-sm font-mono"
                                        data-testid={`universal-na-dep-${i}-cpf`} />
                                    </div>
                                  </div>
                                </div>
                              );
                            })}
                          </div>
                        )}
                      </>
                    )}
                  </div>
                )}
              </div>
            )}

            {/* Concordo checkbox for non-VT / non-NÃO ADESÃO docs */}
            {!meta.hasChoice && !meta.hasNaoAdesao && (
              <div className="flex items-start gap-2">
                <Checkbox id="universal-agree" checked={agreed}
                  onCheckedChange={(v) => setAgreed(!!v)}
                  data-testid="universal-agree-check" />
                <Label htmlFor="universal-agree" className="text-sm leading-snug">
                  <strong>Li e concordo</strong> com os termos deste documento.
                </Label>
              </div>
            )}

            {/* Channel chooser */}
            {faceEnabled && (
              <>
                <div className="rounded-md border-2 border-indigo-200 bg-indigo-50/60 p-2.5">
                  <button
                    type="button"
                    disabled={busy || !canSend()}
                    onClick={() => {
                      const needsRubrica =
                        kind === "vacation_notice" || kind === "vacation_advance";
                      if (needsRubrica && !user?.has_signature) {
                        setFaceIntent(true);
                        setRubricaOpen(true);
                      } else {
                        setStep("face");
                      }
                    }}
                    className="w-full flex items-center justify-center gap-2 rounded-md bg-indigo-600 hover:bg-indigo-700 disabled:opacity-50 disabled:cursor-not-allowed text-white font-semibold text-sm py-2.5 transition"
                    data-testid="universal-face-btn"
                  >
                    <ScanFace className="w-5 h-5" />
                    Assinar com meu rosto (mais fácil)
                  </button>
                  <div className="text-[10px] text-indigo-900/70 mt-1 text-center">
                    Sem código: tire uma selfie e pronto. Marque "Li e concordo" acima para liberar.
                  </div>
                </div>
                <div className="flex items-center gap-2">
                  <div className="flex-1 h-px bg-slate-200" />
                  <span className="text-[10px] text-slate-400 uppercase">ou receba um código</span>
                  <div className="flex-1 h-px bg-slate-200" />
                </div>
              </>
            )}
            <div className="rounded-md border border-slate-200 bg-slate-50 p-3 text-xs">
              <div className="text-slate-500 mb-1">Como deseja receber o código?</div>
              <div className="grid grid-cols-2 gap-2 mt-1">
                <button type="button" onClick={() => setChannel("sms")} disabled={!canSms}
                  className={`text-left rounded-md border p-2 transition ${
                    channel === "sms" ? `${cc.borderActive} bg-white shadow-sm`
                                       : "border-slate-200 bg-white/50 hover:bg-white"
                  } ${!canSms ? "opacity-50 cursor-not-allowed" : ""}`}>
                  <div className={`text-xs font-semibold ${cc.text} flex items-center gap-1`}>
                    <Phone className="w-3 h-3" /> SMS
                  </div>
                  <div className="text-[10px] text-slate-600 truncate font-mono">
                    {registeredPhone || "Sem telefone"}
                  </div>
                </button>
                <button type="button" onClick={() => setChannel("email")} disabled={!canEmail}
                  className={`text-left rounded-md border p-2 transition ${
                    channel === "email" ? "border-blue-600 bg-white shadow-sm"
                                         : "border-slate-200 bg-white/50 hover:bg-white"
                  } ${!canEmail ? "opacity-50 cursor-not-allowed" : ""}`}>
                  <div className="text-xs font-semibold text-blue-700 flex items-center gap-1">
                    <Mail className="w-3 h-3" /> E-mail
                  </div>
                  <div className="text-[10px] text-slate-600 truncate">
                    {registeredEmail || "Sem e-mail"}
                  </div>
                </button>
              </div>
            </div>
          </motion.div>
        )}

        {step === "face" && (
          <FaceSignCapture
            onCancel={() => setStep("preview")}
            onSubmit={async (selfie) => {
              if (kind === "vacation_notice") {
                await axios.post(
                  `${API}/employee/${meta.base}/${id}/sign-face`,
                  { token, selfie_base64: selfie, consent: true },
                  { timeout: 60000 },
                );
              } else {
                await axios.post(
                  `${API}/employee/${meta.base}/${id}/confirm-otp`,
                  { token, face: true, selfie_base64: selfie, consent: true,
                    ...(meta.piece ? { piece: meta.piece } : {}),
                    ...((meta.hasChoice || meta.hasNaoAdesao)
                      ? { choice: buildChoicePayload() } : {}) },
                  { timeout: 60000 },
                );
              }
              setStep("done");
              toast.success("Rosto verificado — documento assinado!");
              onSigned?.();
            }}
          />
        )}

        {step === "otp" && (
          <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} className="space-y-3">
            <div className="rounded-md border border-emerald-200 bg-emerald-50 p-3">
              <ShieldCheck className="inline w-4 h-4 mr-1 text-emerald-700" />
              <span className="text-sm text-emerald-900">
                Enviamos um código para{" "}
                {maskedTo || (channel === "email" ? maskEmail(registeredEmail) : maskPhone(registeredPhone))}.
              </span>
            </div>
            <Label htmlFor="universal-otp">
              Código de verificação ({channel === "email" ? "e-mail" : "SMS"})
            </Label>
            <div className="relative">
              <KeyRound className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
              <Input id="universal-otp" inputMode="numeric" maxLength={10}
                placeholder="000000" className="pl-9 tracking-widest font-mono text-lg"
                value={code}
                onChange={(e) => setCode(e.target.value.replace(/\D/g, ""))}
                data-testid="universal-otp-input" />
            </div>
          </motion.div>
        )}

        {step === "done" && (
          <motion.div initial={{ opacity: 0, scale: 0.95 }} animate={{ opacity: 1, scale: 1 }}
                      className="text-center py-6 space-y-3">
            <CheckCircle2 className="w-16 h-16 text-emerald-600 mx-auto" />
            <h3 className="text-lg font-bold text-emerald-900">Documento assinado</h3>
            <p className="text-sm text-slate-600">
              {meta.label} registrada com validade jurídica.
            </p>
          </motion.div>
        )}

        <DialogFooter>
          {step === "preview" && (
            <>
              {!blocking && (
                <Button variant="outline" onClick={onClose} disabled={busy}>
                  Cancelar
                </Button>
              )}
              <Button
                onClick={() =>
                  user?.has_signature ? sendOtp() : setRubricaOpen(true)
                }
                disabled={busy || !canSend()}
                className={cc.primaryBtn}
                data-testid="universal-send-otp-btn">
                {busy ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <KeyRound className="w-4 h-4 mr-2" />}
                Enviar código por {channel === "email" ? "e-mail" : "SMS"}
              </Button>
            </>
          )}
          {step === "otp" && (
            <>
              <Button variant="outline" onClick={() => setStep("preview")} disabled={busy}>Voltar</Button>
              <Button onClick={confirmOtp} disabled={busy || code.length < 4}
                className="bg-emerald-600 hover:bg-emerald-700 text-white"
                data-testid="universal-confirm-otp-btn">
                {busy ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <CheckCircle2 className="w-4 h-4 mr-2" />}
                Confirmar e assinar
              </Button>
            </>
          )}
          {step === "done" && (
            <>
              <Button variant="outline" onClick={download}
                data-testid="universal-download-btn">
                <Download className="w-4 h-4 mr-2" /> Baixar PDF
              </Button>
              <Button onClick={onClose}
                className={cc.primaryBtn}>
                Continuar
              </Button>
            </>
          )}
        </DialogFooter>

        <SignatureEditDialog
          open={rubricaOpen}
          onOpenChange={setRubricaOpen}
          hasExisting={!!user?.has_signature}
          onSaved={() => {
            if (faceIntent) {
              setFaceIntent(false);
              setStep("face");
            } else if (step === "preview") sendOtp();
            else toast.info("Rúbrica salva — toque em Confirmar e assinar.");
          }}
        />
      </DialogContent>
    </Dialog>
  );
}
